import * as yup from 'yup'

export interface ErrorObject {
  [key: string]: string | ErrorObject
}

export interface ErrorInput {
  [key: string]: string
}

export const createObjectFromPath = (
  obj: object,
  path: string | string[],
  value: any = undefined
) => {
  let newPath = typeof path === 'string' ? path.split('.') : path
  let current: any = obj
  while (newPath.length > 1) {
    const [head, ...tail] = newPath
    newPath = tail
    if (current[head] === undefined) {
      current[head] = {}
    }
    current = current[head]
  }
  current[newPath[0]] = value
  return obj
}

export const createErrorObject = (errors: ErrorInput): ErrorObject => {
  let errorObject = {}
  for (const keyName in errors) {
    if (keyName && errors[keyName]) {
      const newObj = createObjectFromPath(errorObject, keyName, errors[keyName])
      errorObject = {
        ...newObj,
      }
    }
  }
  return errorObject
}

export type ErrorsOutput<T extends object> = {
  [keyName in keyof T]: string
}

interface IValidatorOutput<T extends object> {
  errors?: ErrorsOutput<T>
}

/**
 * Receives any type of yup.Schema object and
 * compares/validates `formData`
 */
export const schemaValidator = <T extends object>(
  schema: yup.ObjectSchemaDefinition<T>,
  formData: object,
  extraSchema?: yup.ObjectSchemaDefinition<Partial<T>>
): IValidatorOutput<T> => {
  let shape = { ...schema }

  if (extraSchema) {
    shape = {
      ...shape,
      ...extraSchema,
    }
  }

  const fullSchema = yup.object(shape)

  try {
    fullSchema.validateSync(formData, { abortEarly: false })
    return {}
  } catch (errs) {
    const validationMessages = errs.inner.reduce(
      (acc: { [key: string]: string }, { message, path }: { path: string; message: string }) => {
        acc[path] = message
        return acc
      },
      {}
    )
    return { errors: validationMessages }
  }
}

/**
 * Simple way to store schema object and validate results
 * as well as return original
 */
export class SchemaRules<T extends object> {
  public schema: yup.ObjectSchemaDefinition<T>

  constructor(schema: yup.ObjectSchemaDefinition<T>) {
    this.schema = schema
  }

  /**
   * Optionally pass in an `ObjectSchemaDefinition` object that
   * overrides the base `Schema` for that request
   */
  validate = (
    formData: object,
    extraSchema?: yup.ObjectSchemaDefinition<Partial<T>>
  ): IValidatorOutput<T> => {
    return schemaValidator<T>(this.schema, formData, extraSchema)
  }
}
